import { useState } from 'react'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  assignmentDateFiltersChanged,
  selectAssignmentDateFilters,
  type AssignmentSummaryV3,
} from 'src/v3/manage/manage.slice'

export type StatusFilter = 'All' | 'Active' | 'Overdue' | 'Completed'

export type AssignmentFilters = {
  nameFilter: string
  linkedServiceFilter: string
  statusFilter: StatusFilter
  dateLowerFilter: string
  dateUpperFilter: string
}

export type AssignmentFiltersActions = {
  setNameFilter: (value: string) => void
  setLinkedServiceFilter: (value: string) => void
  setStatusFilter: (value: StatusFilter) => void
  setDateLowerFilter: (value: any) => void
  setDateUpperFilter: (value: any) => void
  resetFilters: () => void
}

export default function useAssignmentFilters() {
  let dispatch = useAppDispatch()
  let dateFilters = useAppSelector(selectAssignmentDateFilters)

  let [nameFilter, setNameFilter] = useState<string>('')
  let [linkedServiceFilter, setLinkedServiceFilter] = useState<string>('')
  let [statusFilter, setStatusFilter] = useState<StatusFilter>('All')

  let setDateLowerFilter = (value: any) => {
    let dateStr = value
      ? dayjs.isDayjs(value)
        ? value.toISOString()
        : dayjs(value).toISOString()
      : null
    dispatch(assignmentDateFiltersChanged({ startDate: dateStr }))
  }

  let setDateUpperFilter = (value: any) => {
    let dateStr = value
      ? dayjs.isDayjs(value)
        ? value.toISOString()
        : dayjs(value).toISOString()
      : null
    dispatch(assignmentDateFiltersChanged({ endDate: dateStr }))
  }

  let resetFilters = () => {
    setNameFilter('')
    setStatusFilter('All')
    dispatch(
      assignmentDateFiltersChanged({
        startDate: null,
        endDate: null,
      }),
    )
  }

  let filterAssignments = (assignments: AssignmentSummaryV3[]) => {
    let statusMatchers = {
      Completed: (assignment: AssignmentSummaryV3) =>
        assignment.completionPercentage === 100,
      Overdue: (assignment: AssignmentSummaryV3) =>
        assignment.dueDate &&
        dayjs(assignment.dueDate).isBefore(dayjs(), 'day') &&
        assignment.completionPercentage !== 100,
      Active: (assignment: AssignmentSummaryV3) =>
        assignment.completionPercentage < 100 &&
        (!assignment.dueDate ||
          dayjs(assignment.dueDate).isAfter(dayjs(), 'day')),
      All: () => true,
    }

    return assignments.filter((assignment) => {
      let matchesName = assignment.name
        .toLowerCase()
        .includes(nameFilter.toLowerCase())
      let matchesLinkedService =
        !linkedServiceFilter ||
        assignment.linkedServiceID === linkedServiceFilter
      let matchesStatus = statusMatchers[statusFilter](assignment)

      // Filter by due date if assignment has a due date
      let matchesDateRange = assignment.dueDate
        ? (() => {
            let dueDate = dayjs(assignment.dueDate)
            let lowerDate = dayjs(dateFilters.startDate)
            let upperDate = dayjs(dateFilters.endDate)

            return (
              dueDate.isAfter(lowerDate.subtract(1, 'day')) &&
              dueDate.isBefore(upperDate.add(1, 'day'))
            )
          })()
        : true

      return (
        matchesName && matchesLinkedService && matchesStatus && matchesDateRange
      )
    })
  }

  let filters: AssignmentFilters = {
    nameFilter,
    linkedServiceFilter,
    statusFilter,
    dateLowerFilter: dateFilters.startDate,
    dateUpperFilter: dateFilters.endDate,
  }

  let actions: AssignmentFiltersActions = {
    setNameFilter,
    setLinkedServiceFilter,
    setStatusFilter,
    setDateLowerFilter,
    setDateUpperFilter,
    resetFilters,
  }

  return {
    filters,
    actions,
    filterAssignments,
  }
}
